/**
 * Documents domain API (Spec 059, E2): CRUD, listing, correlation, duplicates, browse tree,
 * feedback. Moved bitgleich from the former monolithic `api.ts` — no behavior change (E5).
 */
import type { ApiCore, PaginatedResponse } from './core';
import type { Followup } from './followups';
import { authStore } from '../auth';

// Session memo of the promise per `id|size` (Spec 111 E5): the same preview shows up in list,
// detail and stack. A failure is dropped so a not-yet-generated preview can be asked again.
const thumbnailUrls = new Map<string, Promise<string>>();

// Logout / account switch: the remembered URLs belong to the previous session (Spec 111 L3).
// Only the transition to null counts; the initial value triggers nothing.
let signedIn: boolean | undefined;
authStore.subscribe((user) => {
  if (signedIn && !user) thumbnailUrls.clear();
  signedIn = !!user;
});

export interface Document {
  id: string;
  fileName: string;
  displayName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  referenceDate?: string;
  uploadedBy: string;
  status: 'Uploading' | 'Processing' | 'Finished' | 'Deleted';
  classification?: {
    category: string;
    subCategory: string;
    confidence: number;
    isManualClassified?: boolean;
  };
  systemTags: string[];
  userTags: string[];
  assignedTo: string[];
  extractedData?: {
    extractedValues: Record<string, string>;
    extractedAt: string;
    extractorVersion: string;
  };
  thumbnailUris?: {
    medium: string;
    large: string;
  };
  householdId: string;
  slug?: string;
  ocrText?: string;
  duplicateOf?: string;
  notes?: string;
  retentionYears?: number;
  retainUntil?: string;
  /** When the document was moved to the bin (soft delete). Undefined when not deleted (Spec 074/E6b). */
  deletedAt?: string;
  /**
   * The document's open Wiedervorlage, if any (Spec 085 E13). Never set for share-link recipients.
   * Type only — the surface (Spec 086) reads and renders this.
   */
  activeFollowup?: {
    id: string;
    dueDate: string;
    subject: string;
    note?: string;
  };
  /**
   * An AI-suggested hard deadline, present only while it is Open (Spec 087, #486 E10). `null`/
   * absent once accepted, dismissed, or never suggested.
   */
  deadlineSuggestion?: {
    date: string;
    subject: string;
    confidence: number;
  } | null;
}

/**
 * Bin list projection (Spec 073/E1, Spec 074/E3): the seven business fields the PO approved
 * for filtering/sorting in the bin view, plus id/slug.
 */
export interface BinnedDocument {
  id: string;
  slug?: string;
  referenceDate?: string;
  uploadedAt: string;
  subject?: string;
  merchant?: string;
  amount?: number;
  category?: string;
  deletedAt?: string;
}

export interface RestoreDocumentsBatchResult {
  restoredCount: number;
  totalCount: number;
}

export interface Category {
  name: string;
  displayName: string;
  displayNameEn?: string;
  localizedName?: string;
  subCategories: SubCategory[];
  // Spec 010: optional ordered list of visible column keys for this category.
  // Seit Spec 010 mappt CategoriesController das Feld.
  listColumns?: string[];
}

export interface SubCategory {
  name: string;
  displayName: string;
  displayNameEn?: string;
  localizedName?: string;
}

export interface DashboardStats {
  totalDocumentsHousehold: number;
  totalDocumentsGlobal: number;
  processingDocuments: number;
  unreviewedDocuments: number;
}

// === Feedback Types (Issue #106) ===

export interface FeedbackResponse {
  id: string;
  documentId: string;
  category: string;
  freeText?: string;
  createdAt: string;
}

export class DocumentsApi {
  constructor(private readonly core: ApiCore) {}

  /** Get document by ID */
  async getDocument(
    id: string,
    opts?: { suppressGlobalErrorHandler?: boolean },
  ): Promise<Document> {
    return this.core.fetchJson<Document>(
      `${this.core.baseUrl}/api/v1/documents/${id}`,
      { headers: this.core.getAuthHeaders() },
      opts?.suppressGlobalErrorHandler ?? false,
    );
  }

  /** Delete document */
  async deleteDocument(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${id}`, {
      method: 'DELETE',
      headers: this.core.getAuthHeaders(),
    });
  }

  /** Update classification (category + sub-category) for a document. */
  async updateDocumentClassification(
    id: string,
    category: string,
    subCategory: string,
  ): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${id}/classification`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({ category, subCategory }),
    });
  }

  /** Update person assignments for a document. */
  async updateDocumentAssignedTo(id: string, assignedTo: string[]): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${id}/assigned-to`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify(assignedTo),
    });
  }

  /** Atomically update document metadata (assignedTo, userTags, householdId). */
  async updateDocumentMetadata(
    id: string,
    metadata: {
      assignedTo?: string[];
      userTags?: string[];
      householdId?: string;
      extractedValues?: Record<string, string>;
      notes?: string;
    },
  ): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${id}/metadata`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify(metadata),
    });
  }

  /** Reprocess a document, triggering specified processing steps again. */
  async reprocessDocument(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${id}/reprocess`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({
        reclassify: true,
        reOcr: true,
        reExtractData: true,
        reGenerateThumbnails: true,
      }),
    });
  }

  /** Signed download URL (1 h). Signed-in only (Spec 111) — `window.open` cannot send the Bearer. */
  getDownloadSignedUrl(id: string, disposition?: 'attachment' | 'inline'): Promise<string> {
    // Spec 123: 'attachment' saves the file under its display name; no argument = inline (tab).
    const query = disposition === 'attachment' ? '?disposition=attachment' : '';
    return this.fetchSignedUrl(`${id}/download-url${query}`);
  }

  /** Page images (Spec 117 E5): up to 20 signed WebP URLs; `totalPages` may exceed `pages.length`. */
  getPageImageUrls(id: string): Promise<{
    totalPages: number;
    pages: Array<{ page: number; url: string }>;
  }> {
    // Global error toast off: the preview falls back to the thumbnail silently (Spec 118 E2).
    return this.core.fetchJson(
      `${this.core.baseUrl}/api/v1/documents/${id}/pages/url`,
      { headers: this.core.getAuthHeaders() },
      true,
    );
  }

  /** Signed thumbnail URL (24 h), memoised per id and size. */
  getThumbnailSignedUrl(id: string, size: 'medium' | 'large'): Promise<string> {
    const key = `${id}|${size}`;
    let pending = thumbnailUrls.get(key);
    if (!pending) {
      pending = this.fetchSignedUrl(`${id}/thumbnail/${size}/url`).catch((error) => {
        thumbnailUrls.delete(key);
        throw error;
      });
      thumbnailUrls.set(key, pending);
    }
    return pending;
  }

  /** Drop a remembered thumbnail URL, e.g. after the <img> failed to load it (expired). */
  forgetThumbnailSignedUrl(id: string, size: 'medium' | 'large'): void {
    thumbnailUrls.delete(`${id}|${size}`);
  }

  private async fetchSignedUrl(path: string): Promise<string> {
    // Global error toast off: callers show their own state (error toast, no-preview symbol).
    const dto = await this.core.fetchJson<{ url: string }>(
      `${this.core.baseUrl}/api/v1/documents/${path}`,
      { headers: this.core.getAuthHeaders() },
      true,
    );
    return dto.url;
  }

  /** Get dashboard statistics */
  async getDashboardStats(householdId?: string): Promise<DashboardStats> {
    const searchParams = new URLSearchParams();
    if (householdId) {
      searchParams.append('householdId', householdId);
    }

    return this.core.fetchJson<DashboardStats>(
      `${this.core.baseUrl}/api/v1/documents/stats?${searchParams.toString()}`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /**
   * Accept an open deadline suggestion, creating a Wiedervorlage for it (Spec 087, #486 E11).
   * Suppresses the global error handler — the caller (review card) shows the translated message
   * inline instead of a toast (E15/S31).
   */
  async acceptDeadlineSuggestion(
    id: string,
    payload: { dueDate: string; subject: string },
  ): Promise<Followup> {
    return this.core.fetchJson<Followup>(
      `${this.core.baseUrl}/api/v1/documents/${id}/deadline-suggestion/accept`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.core.getAuthHeaders() },
        body: JSON.stringify(payload),
      },
      /* suppressGlobalErrorHandler */ true,
    );
  }

  /** Dismiss an open deadline suggestion — no Wiedervorlage created (Spec 087, #486 E12). */
  async dismissDeadlineSuggestion(id: string): Promise<void> {
    return this.core.fetchVoid(
      `${this.core.baseUrl}/api/v1/documents/${id}/deadline-suggestion/dismiss`,
      { method: 'POST', headers: this.core.getAuthHeaders() },
      /* suppressGlobalErrorHandler */ true,
    );
  }

  /** Mark document as manually reviewed */
  async reviewDocument(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${id}/review`, {
      method: 'POST',
      headers: this.core.getAuthHeaders(),
    });
  }

  /** Mark multiple documents as reviewed in a single batch request */
  async reviewDocumentsBatch(
    ids: string[],
  ): Promise<{ reviewedCount: number; totalRequested: number }> {
    return this.core.fetchJson(`${this.core.baseUrl}/api/v1/documents/review-batch`, {
      method: 'POST',
      headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentIds: ids }),
    });
  }

  /**
   * Bulk-update metadata for multiple documents in one request (#243).
   * Partial-update semantics: only provided (non-undefined) fields are changed.
   */
  async updateDocumentsMetadataBatch(
    ids: string[],
    metadata: { assignedTo?: string[]; userTags?: string[]; householdId?: string; notes?: string },
  ): Promise<{ updatedCount: number; requestedCount: number }> {
    return this.core.fetchJson(`${this.core.baseUrl}/api/v1/documents/metadata-batch`, {
      method: 'POST',
      headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentIds: ids, ...metadata }),
    });
  }

  /**
   * Delete multiple documents in a single batch request.
   * Uses the dedicated delete-batch endpoint instead of individual calls to avoid
   * BigQuery DML concurrency issues (ADR-047).
   */
  async deleteDocumentsBatch(
    ids: string[],
  ): Promise<{ deletedCount: number; totalRequested: number }> {
    return this.core.fetchJson(`${this.core.baseUrl}/api/v1/documents/delete-batch`, {
      method: 'POST',
      headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentIds: ids }),
    });
  }

  /** List documents with pagination and filters */
  async listDocuments(params?: {
    cursor?: string;
    pageSize?: number;
    category?: string;
    subCategory?: string;
    status?: string;
    isReviewed?: boolean;
    search?: string;
    householdId?: string;
    sortBy?: 'fileName' | 'uploadedAt' | 'referenceDate' | 'fileSize' | 'assignedTo';
    sortOrder?: 'asc' | 'desc';
    fromDate?: string;
    toDate?: string;
    fileFormat?: string;
    minSize?: number;
    maxSize?: number;
    assignedTo?: string;
    userTag?: string;
  }): Promise<PaginatedResponse<Document>> {
    const queryParams = new URLSearchParams();

    if (params?.cursor) queryParams.set('cursor', params.cursor);
    if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString());
    if (params?.category) queryParams.set('category', params.category);
    if (params?.subCategory) queryParams.set('subCategory', params.subCategory);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.isReviewed !== undefined)
      queryParams.set('isReviewed', params.isReviewed.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.householdId) queryParams.set('householdId', params.householdId);
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);
    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);
    if (params?.fileFormat) queryParams.set('fileFormat', params.fileFormat);
    if (params?.minSize != null) queryParams.set('minSize', params.minSize.toString());
    if (params?.maxSize != null) queryParams.set('maxSize', params.maxSize.toString());
    if (params?.assignedTo) queryParams.set('assignedTo', params.assignedTo);
    if (params?.userTag) queryParams.set('userTag', params.userTag);

    return this.core.fetchJson<PaginatedResponse<Document>>(
      `${this.core.baseUrl}/api/v1/documents?${queryParams}`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /** Get documents by category/subcategory */
  async getDocumentsByCategory(
    category: string,
    subCategory?: string,
    params?: {
      cursor?: string;
      pageSize?: number;
      sortBy?: 'fileName' | 'uploadedAt';
      sortOrder?: 'asc' | 'desc';
    },
  ): Promise<PaginatedResponse<Document>> {
    return this.listDocuments({ ...params, category, subCategory });
  }

  /** Search documents */
  async searchDocuments(
    query: string,
    params?: { cursor?: string; pageSize?: number },
  ): Promise<PaginatedResponse<Document>> {
    return this.listDocuments({ ...params, search: query });
  }

  /** Get the bin listing (Spec 073/E1, 074/Betroffene Komponenten): all documents in the bin. */
  async getBin(): Promise<PaginatedResponse<BinnedDocument>> {
    return this.core.fetchJson<PaginatedResponse<BinnedDocument>>(
      `${this.core.baseUrl}/api/v1/documents/bin`,
      { headers: this.core.getAuthHeaders() },
    );
  }

  /** Restore multiple documents from the bin in one batch request (Spec 073/074). */
  async restoreDocuments(ids: string[]): Promise<RestoreDocumentsBatchResult> {
    return this.core.fetchJson<RestoreDocumentsBatchResult>(
      `${this.core.baseUrl}/api/v1/documents/batch/restore`,
      {
        method: 'POST',
        headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentIds: ids }),
      },
    );
  }

  /** Get available categories */
  async getCategories(): Promise<Category[]> {
    return this.core.fetchJson<Category[]>(`${this.core.baseUrl}/api/v1/categories`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  // === Feedback API ===

  /** Submit feedback for a document (Issue #106) */
  async submitFeedback(
    documentId: string,
    category: string,
    freeText?: string,
    documentCategory?: string,
    documentConfidence?: number,
    householdId?: string,
  ): Promise<FeedbackResponse> {
    return this.core.fetchJson<FeedbackResponse>(`${this.core.baseUrl}/api/v1/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({
        documentId,
        category,
        freeText: freeText || null,
        documentCategory: documentCategory || null,
        documentConfidence: documentConfidence ?? null,
        householdId: householdId || null,
      }),
    });
  }
}
